import type { OnboardingResource } from "@shared/schema";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";
import { orpc } from "@/lib/rpc";
import type { AssignmentWithResource, MilestonesDataSource } from "./data-source";

// Snapshot-side data source: reads/writes the onboarding_assignments and
// onboarding_sidebar_* tables. Used by the customize page (UC-ONB-014).

export function useSnapshotMilestonesDataSource(
  onboardingId: string,
  options: { isOwner: boolean; templateId: string | null },
): MilestonesDataSource {
  const queryClient = useQueryClient();
  const [pickerEnabled, setPickerEnabled] = useState(false);

  const assignmentsQueryOptions = orpc.onboardingSnapshotAssignments.list.queryOptions({
    input: { onboardingId },
  });
  const assignmentsQuery = useQuery({
    ...assignmentsQueryOptions,
    enabled: !!onboardingId,
  });
  const assignments = (assignmentsQuery.data as AssignmentWithResource[] | undefined) ?? [];

  // Resource picker is fed from the global pool — same query the template
  // canvas uses, just without filtering by templateId (the API accepts a
  // missing templateId and returns the full pool).
  const resourcesQueryOptions = orpc.onboardingResources.list.queryOptions({
    input: { templateId: options.templateId ?? "" },
  });
  const resourcesQuery = useQuery({
    ...resourcesQueryOptions,
    enabled: !!onboardingId && !!options.templateId && pickerEnabled,
  });
  const resources = (resourcesQuery.data as OnboardingResource[] | undefined) ?? [];

  const addMutation = useMutation(orpc.onboardingSnapshotAssignments.add.mutationOptions());
  const moveMutation = useMutation(orpc.onboardingSnapshotAssignments.move.mutationOptions());
  const setScopeMutation = useMutation(
    orpc.onboardingSnapshotAssignments.setScope.mutationOptions(),
  );
  const removeMutation = useMutation(orpc.onboardingSnapshotAssignments.remove.mutationOptions());

  const invalidate = useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: assignmentsQueryOptions.queryKey }),
      queryClient.invalidateQueries({ queryKey: resourcesQueryOptions.queryKey }),
      queryClient.invalidateQueries({
        queryKey: orpc.preview.getOnboardingPreview.queryOptions().queryKey,
      }),
    ]);
  }, [queryClient, assignmentsQueryOptions.queryKey, resourcesQueryOptions.queryKey]);

  const isMutationPending =
    addMutation.isPending ||
    moveMutation.isPending ||
    setScopeMutation.isPending ||
    removeMutation.isPending;

  return useMemo<MilestonesDataSource>(
    () => ({
      assignments,
      resources,
      isLoading: assignmentsQuery.isLoading,
      isMutationPending,
      pickerEnabled,
      setPickerEnabled,

      async addAssignment(input) {
        if (!options.isOwner) return null;
        const created = await addMutation.mutateAsync({ ...input, onboardingId });
        return (created as unknown as AssignmentWithResource) ?? null;
      },
      async moveAssignment(input) {
        if (!options.isOwner) return;
        await moveMutation.mutateAsync(input);
      },
      async setScope(input) {
        if (!options.isOwner) return;
        await setScopeMutation.mutateAsync(input);
      },
      async removeAssignment(input) {
        if (!options.isOwner) return;
        await removeMutation.mutateAsync(input);
      },

      invalidate,
    }),
    [
      assignments,
      resources,
      assignmentsQuery.isLoading,
      isMutationPending,
      pickerEnabled,
      addMutation,
      moveMutation,
      setScopeMutation,
      removeMutation,
      invalidate,
      onboardingId,
      options.isOwner,
    ],
  );
}
